import { FontAwesome } from "@expo/vector-icons";
import { t } from "i18next";
import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { Pressable, Text, TextInput, View } from "react-native";
import { DatePickerModal } from "react-native-paper-dates";

interface InputDateProps {
  name: string;
  control: any;
  label: string;
  rules?: object;
  value?: string;
  handleChange?: (text: string) => void;
  optional?: boolean;
}

export default function InputDate(props: InputDateProps) {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [open, setOpen] = useState(false);

  const formatInput = (text: string) => {
    const numericValue = text.replace(/[^\d]/g, "");
    return (
      numericValue.slice(0, 4) +
      (numericValue.length > 4 ? `/${numericValue.slice(4, 6)}` : "") +
      (numericValue.length > 6 ? `/${numericValue.slice(6, 8)}` : "")
    );
  };

  const formatDateValue = (value: string) => {
    if (!value) return "";
    return value.replace(/-/g, "/");
  };

  const convertToValidatorFormat = (text: string) => {
    if (!text) return "";
    return text.replace(/\//g, "-");
  };

  const handleDate = (date, onChange) => {
    if (!date) {
      setOpen(false);
      return;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const formattedValidationDate = `${year}/${month}/${day}`;

    setDate(date);
    onChange(convertToValidatorFormat(formattedValidationDate));
    setOpen(false);

    props.handleChange?.(convertToValidatorFormat(formattedValidationDate));
  };

  return (
    <View>
      {props.optional === true ? (
        <View className="flex-row m-2">
          <Text className="text-black text-sm">{props.label}</Text>
          <Text className="text-midgray text-sm italic">{` (${t(
            "common:placeholder.optional",
          )})`}</Text>
        </View>
      ) : (
        <Text className="text-black text-sm m-2">{props.label}</Text>
      )}
      <Controller
        control={props.control}
        name={props.name}
        rules={props.rules}
        defaultValue=""
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <>
            <DatePickerModal
              locale="en"
              mode="single"
              visible={open}
              onDismiss={() => setOpen(false)}
              date={date}
              onConfirm={(params) => handleDate(params.date, onChange)}
              presentationStyle="pageSheet"
              label={t("common:placeholder.selectdate")}
              saveLabel={t("common:button.save")}
            />
            <View className="relative flex-row items-center">
              <TextInput
                ref={ref}
                className={`flex-1 border border-solid rounded-xl w-80 h-14 px-4 py-6 text-black1 ${
                  error ? "border-red" : "border-midgray"
                }`}
                value={formatDateValue(value)}
                onChangeText={(text) => {
                  const formattedText = formatInput(text);
                  onChange(convertToValidatorFormat(formattedText));
                  props.handleChange?.(formattedText);
                }}
                placeholder="YYYY/MM/DD"
                placeholderTextColor="#BDBDBD"
              />
              <Pressable
                onPress={() => setOpen(true)}
                className="absolute right-3 p-2"
              >
                <FontAwesome name="calendar-o" size={24} color="#BDBDBD" />
              </Pressable>
            </View>
            <View style={{ height: 20, overflow: "hidden" }}>
              {error && (
                <Text className="text-red text-xs pl-2">{error.message}</Text>
              )}
            </View>
          </>
        )}
      />
    </View>
  );
}
